import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  Bell, X, UserPlus, MapPin, Award, Send, Trophy, XCircle, Car, MessageSquare,
  Briefcase, Play, CheckCircle, Crosshair, FileCheck, Receipt, DollarSign, Percent,
} from "lucide-react";

const POLL_INTERVAL = 20000;

const ICON_MAP = {
  "user-plus": UserPlus, "map-pin": MapPin, "award": Award, "send": Send,
  "trophy": Trophy, "x-circle": XCircle, "car": Car, "message-square": MessageSquare,
  "briefcase": Briefcase, "play": Play, "check-circle": CheckCircle, "crosshair": Crosshair,
  "file-check": FileCheck, "receipt": Receipt, "dollar-sign": DollarSign, "percent": Percent,
};

const COLOR_MAP = {
  blue: "bg-blue-500/15 text-blue-300",
  cyan: "bg-cyan-500/15 text-cyan-300",
  amber: "bg-amber-500/15 text-amber-300",
  violet: "bg-violet-500/15 text-violet-300",
  emerald: "bg-emerald-500/15 text-emerald-300",
  rose: "bg-rose-500/15 text-rose-300",
  slate: "bg-slate-700/40 text-slate-300",
};

function timeAgo(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  const poll = async () => {
    try {
      const [list, count] = await Promise.all([
        api.get("/notifications/inbox", { params: { limit: 25 } }),
        api.get("/notifications/unread-count"),
      ]);
      setItems(list.data || []);
      setUnreadCount(count.data?.count || 0);
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    poll();
    const t = setInterval(poll, POLL_INTERVAL);
    return () => clearInterval(t);
  }, []);

  const onClick = async (n) => {
    setOpen(false);
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch (e) { /* ignore */ }
    }
    if (n.link) navigate(n.link);
    poll();
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      poll();
    } catch (e) { /* ignore */ }
  };

  return (
    <div className="relative">
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-lg hover:bg-slate-800/60 transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4 text-slate-300" />
        {unreadCount > 0 && (
          <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-12 w-96 rounded-xl border border-slate-800 bg-[#0B1120] shadow-2xl z-40 overflow-hidden" data-testid="notification-panel">
            <div className="flex items-center justify-between p-4 border-b border-slate-800">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">Notifications {unreadCount > 0 && <span className="text-blue-400">({unreadCount} new)</span>}</div>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button onClick={markAll} className="text-xs text-blue-400 hover:underline" data-testid="mark-all-read">Mark all read</button>
                )}
                <Link to="/notifications" onClick={() => setOpen(false)} className="text-xs text-slate-400 hover:text-slate-200" data-testid="see-all-notifications">See all</Link>
                <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-300"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {items.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-500" data-testid="notifications-empty">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" /> No notifications
                </div>
              ) : (
                items.map((n) => {
                  const Icon = ICON_MAP[n.icon] || Bell;
                  const colorClass = COLOR_MAP[n.color] || COLOR_MAP.slate;
                  return (
                    <button
                      key={n.id}
                      onClick={() => onClick(n)}
                      className={`w-full text-left flex items-start gap-3 p-3 border-b border-slate-800/60 hover:bg-slate-800/30 transition-colors ${!n.read ? "bg-blue-500/5" : ""}`}
                      data-testid={`notification-${n.id}`}
                    >
                      <div className={`w-8 h-8 rounded-lg ${colorClass} flex items-center justify-center shrink-0`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-100 truncate">{n.title}</div>
                        {n.message && <div className="text-xs text-slate-400 mt-0.5 line-clamp-2">{n.message}</div>}
                        <div className="text-[10px] text-slate-500 mt-1">{timeAgo(n.created_at)}{n.actor ? ` · by ${n.actor}` : ""}</div>
                      </div>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-blue-400 shrink-0 mt-2" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
